import { createContext, useContext, useState, useEffect, ReactNode } from "react";

// Same-origin: the waggle-app server proxies these paths to waggle-api (server/apiProxy.ts),
// so login and every other route validate against the same API.

interface User {
  id: number;
  email: string;
  login_type?: "password" | "google";
  picture_url?: string | null;
  first_name?: string;
  last_name?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem("auth_token");

      if (!savedToken) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch("/v1/users/me", {
          headers: { Authorization: `Bearer ${savedToken}` },
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setToken(savedToken);
        } else {
          localStorage.removeItem("auth_token");
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        localStorage.removeItem("auth_token");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Shared tail of every login method: persist the token, then load the profile
  const startSession = async (newToken: string) => {
    // Save token immediately to prevent race conditions
    localStorage.setItem("auth_token", newToken);
    setToken(newToken);

    const userResponse = await fetch("/v1/users/me", {
      headers: { Authorization: `Bearer ${newToken}` },
    });

    if (!userResponse.ok) {
      throw new Error("Failed to fetch user profile");
    }

    setUser(await userResponse.json());
  };

  const login = async (email: string, password: string) => {
    const response = await fetch("/v1/users/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Login failed");
    }

    const { token: newToken } = await response.json();
    await startSession(newToken);
  };

  const loginWithGoogle = async (credential: string) => {
    const response = await fetch("/v1/users/google", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ credential }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || "Google sign-in failed");
    }

    const { token: newToken } = await response.json();
    await startSession(newToken);
  };

  const logout = async () => {
    if (token) {
      try {
        await fetch("/v1/users/logout", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error("Logout failed:", error);
      }
    }

    setToken(null);
    setUser(null);
    localStorage.removeItem("auth_token");
  };

  return (
    <AuthContext.Provider value={{ user, token, isLoading, login, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
